import { Grid, Link as MaterialLink } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import EndorsementsApi from '../../../api/EndorsementsApi';
import { AuthData } from '../../../context/auth';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { ErrorData } from '../../../context/ErrorProvider';
import {
	StyledContainer,
	StyledSectionContainer,
	StyledTitle,
} from '../../common';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import PricePreviewChangesTables from '../PricePreviewChangesTables';

interface Props {
	authData: AuthData;
	endorsementData: EndorsementData;
	errorData: ErrorData;
}

/**
 * The price preview page for an endorsement.
 */
const EndorsementPricePreview = (props: Props) => {
	const { endorsementId } = useParams<{ endorsementId: string }>();
	const { authData, endorsementData, errorData } = props;
	const authToken = authData.auth.authorizationToken;

	const { data: priceData, isLoading } = useQuery({
		queryKey: ['price-data', endorsementId, authToken],
		queryFn: () =>
			EndorsementsApi.previewEndorsementPrice(authToken, endorsementId),
		onError: (error) => {
			errorData.handleErrorResponse(error, 'Failed to fetch pricing');
		},
	});

	const { policy, productConfig, endorsementUpdateObj } = endorsementData;

	const renderPriceTables = () => {
		if (!priceData) {
			return (
				<div style={{ textAlign: 'center', width: '100%' }}>
					No pricing breakdown available
				</div>
			);
		}
		return (
			<PricePreviewChangesTables
				productConfig={productConfig}
				priceData={priceData.data}
				policy={policy}
				automatedUnderwritingResult={
					endorsementUpdateObj &&
					endorsementUpdateObj.automatedUnderwritingResult
				}
			/>
		);
	};

	return (
		<>
			<Grid container>
				<Grid item xs={12} alignSelf='flex-end' sx={{ textAlign: 'right' }}>
					{authData.hasPermission('auxData.fetch') && (
						<MaterialLink component={RouterLink} to='../auxData'>
							Aux Data
						</MaterialLink>
					)}
				</Grid>
			</Grid>
			<StyledContainer>
				<StyledTitle>Pricing Breakdown</StyledTitle>
			</StyledContainer>
			<hr />
			<StyledSectionContainer>
				{isLoading ? <LargeCenterSpinner /> : renderPriceTables()}
			</StyledSectionContainer>
		</>
	);
};

export default withEndorsementContext<Props>(EndorsementPricePreview);
